"use client";

import { PortableText } from "next-sanity";
import type { PortableTextBlock } from "sanity";
import Image from "@/components/common/image/image";
import BaseLink from "@/components/common/base-link/base-link";
import TableRenderer from "@/components/common/sanity/table-renderer";
import Ad, { AdStyle } from "@/components/resources/resource/resource-ads";
import imageUrl from "@/sanity/utilities/imageUrl";
import { ArticleData, GlossaryTermData } from "@/sanity/interfaces";
import css from "../resource.module.scss";
// InternalLink must be statically imported so engine-written internalLink marks
// are server-rendered; dynamic({ ssr: false }) stripped every in-body internal
// link from the crawlable HTML (AD-36722 regression).
import InternalLink from "@/components/common/internal-link";
import { sanitizeHeadingId } from "@/utilities/sanitizeHeadingId";
import { sanitizeLinkSpans } from "@/utilities/sanitizeLinkSpans";
import ProductFact from "../../../products/product-fact";
import { useEffect, useRef, useState } from "react";
import RichTableRenderer from "@/components/common/sanity/rich-table-renderer/rich-table-renderer";

/* -------------------------------------------------------------------------- */
/*                                   CONFIG                                   */
/* -------------------------------------------------------------------------- */
const WORD_1K = 1000;
const WORD_2K = 2000;
/* -------------------------------------------------------------------------- */
/*                              WORD COUNT UTILS                              */
/* -------------------------------------------------------------------------- */

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}
function countWordsInBlock(block: PortableTextBlock): number {
  if (!block?.children || !Array.isArray(block.children)) return 0;
  return (block.children as Array<{
    text?: string;
  }>).reduce((sum, child) => {
    if (typeof child?.text === "string") {
      return sum + countWords(child.text);
    }
    return sum;
  }, 0);
}

/* -------------------------------------------------------------------------- */
/*                          BLOCK TYPE IDENTIFIERS                             */
/* -------------------------------------------------------------------------- */

function isHeadingBlock(block: PortableTextBlock): boolean {
  return block?._type === "block" && typeof block.style === "string" && /^h[1-6]$/.test(block.style);
}
function isListBlock(block: PortableTextBlock): boolean {
  return Boolean(block?.listItem);
}
function isSafeAdBoundary(block: PortableTextBlock): boolean {
  if (!block) return false;
  if (isHeadingBlock(block)) return false;
  if (isListBlock(block)) return false;
  return true;
}

/* -------------------------------------------------------------------------- */
/*                           AD COUNT & SPLITTING                              */
/* -------------------------------------------------------------------------- */

function getAdCount(totalWords: number): number {
  if (totalWords < WORD_1K) return 1;
  if (totalWords <= WORD_2K) return 2;
  return 3; // MAX ads
}
function findSafeInsertionIndex(blocks: PortableTextBlock[], targetIndex: number): number | null {
  let index = Math.min(targetIndex, blocks.length - 1);

  // Insert BEFORE heading
  if (isHeadingBlock(blocks[index])) {
    return index;
  }

  // Move to end of list
  if (isListBlock(blocks[index])) {
    let i = index;
    while (i < blocks.length && isListBlock(blocks[i])) {
      i++;
    }
    return i;
  }

  // Normal safe boundary
  if (isSafeAdBoundary(blocks[index])) {
    return index + 1;
  }
  return null;
}
function getAdInsertionIndexes(blocks: PortableTextBlock[], adCount: number): number[] {
  if (!adCount || !blocks.length) return [];
  const blockWordCounts = blocks.map(countWordsInBlock);
  const totalWords = blockWordCounts.reduce((a, b) => a + b, 0);

  /**
   * Even splitting:
   * 3 ads → 4 segments
   * Each ad placed after ~25%, 50%, 75% of content
   */
  const segmentSize = totalWords / (adCount + 1);
  const indexes: number[] = [];
  let runningTotal = 0;
  let nextTarget = segmentSize;
  blocks.forEach((_, index) => {
    runningTotal += blockWordCounts[index];
    if (runningTotal >= nextTarget && indexes.length < adCount) {
      const safeIndex = findSafeInsertionIndex(blocks, index);
      if (safeIndex !== null && !indexes.includes(safeIndex)) {
        indexes.push(safeIndex);
      }
      nextTarget += segmentSize;
    }
  });
  return indexes;
}

/* -------------------------------------------------------------------------- */
/*                              MAIN COMPONENT                                */
/* -------------------------------------------------------------------------- */

interface IResourceContentProps {
  data: ArticleData | GlossaryTermData;
}
const ResourceContent = ({
  data
}: IResourceContentProps) => {
  const rsourceData = data as ArticleData;
  const body = rsourceData?.body ?? [];
  const glossData = data as GlossaryTermData;
  const longDefinition = glossData.longDefinition ?? [];
  const dynamicData = rsourceData || glossData;
  const dynamicBodyData = sanitizeLinkSpans(body?.length ? body : longDefinition?.length ? longDefinition : []);
  const conversionAds = (dynamicData?.tags?.[0] as any)?.conversionAds ?? [];
  const fact: ProductFact | null = (dynamicData as any)?.productFact as ProductFact | null;
  if (!dynamicBodyData.length) return null;
  const totalWords = dynamicBodyData.reduce((sum, block) => sum + countWordsInBlock(block), 0);
  const adCount = getAdCount(totalWords);
  const hasKeyTakeaways = Boolean((data as any)?.keyTakeaways?.length);
  const remainingAds = hasKeyTakeaways && adCount > 0 ? adCount - 1 : adCount;
  const adIndexes = getAdInsertionIndexes(dynamicBodyData, remainingAds);

  /***  Start New logic for productfact compoennt placemnt */
  const contentRef = useRef<HTMLDivElement>(null);
  const [factInsertionIndex, setFactInsertionIndex] = useState<number | null>(null);
  useEffect(() => {
    if (!contentRef.current || !fact) {
      return;
    }
    const elements = Array.from(contentRef.current.querySelectorAll(".rt-block")) as HTMLElement[];
    if (!elements.length) {
      return;
    }
    const targetHeight = contentRef.current.scrollHeight * 0.4;
    let insertionIndex: number | null = null;
    let passedTarget = false;
    for (let i = 0; i < elements.length; i++) {
      const type = elements[i].dataset.type;
      const isHeading = type && ["h1", "h2", "h3", "h4", "h5", "h6"].includes(type);
      if (!passedTarget && elements[i].offsetTop >= targetHeight) {
        passedTarget = true;
      }
      if (passedTarget && isHeading) {
        insertionIndex = i;
        break;
      }
    }
    setFactInsertionIndex(insertionIndex);
  }, [dynamicBodyData, fact]);

  /***  end logic for productfact compoennt placemnt */

  /* ---------------------------------------------------------------------- */
  /*                            BUILD ADS ARRAY                              */
  /* ---------------------------------------------------------------------- */

  const ads = conversionAds.map((ad: any) => {
    const adStyleMap: Record<string, AdStyle> = {
      adTypeOne: AdStyle.TYPE_ONE,
      adTypeTwo: AdStyle.TYPE_TWO,
      adTypeThree: AdStyle.TYPE_THREE
    };
    const adStyle = adStyleMap[ad?._type];
    if (!adStyle) return null;
    return <Ad key={ad._key} data={ad} adStyle={adStyle} />;
  }).filter(Boolean);
  let adPointer = 0;
  return <div ref={contentRef} className={`${css.resourceContentBody} ${css.richText}`}>
      {/* If Key Takeaways exist, render the first conversion ad immediately after them */}
      {hasKeyTakeaways && ads[adPointer] && <div className="!w-full">{ads[adPointer++]}</div>}

      {dynamicBodyData.map((block_0, index) => {
      const isH2 = block_0._type === "block" && block_0.style === "h2";
      return <div key={block_0._key} className="rt-block !w-full" data-type={block_0._type === "block" ? block_0.style || "normal" : block_0._type}>
            {isH2 && index > 0 && <div className="my-8 flex items-center gap-4 md:my-10">
                <div className="h-px flex-1 bg-[#D9D8D6]" />

                <Image src="/divider-icon.svg" alt="" width={24} height={25} className="h-6 w-6 shrink-0 md:h-7 md:w-7" />

                <div className="h-px flex-1 bg-[#D9D8D6]" />
              </div>}
            <PortableText onMissingComponent={() => null} value={[block_0]} components={{
          block: {
            h1: ({
              children,
              value
            }) => <h1 id={sanitizeHeadingId((value as {
              headingId?: string;
            })?.headingId)} className="text-[2rem] font-bold text-[#373A36]">
                      {children}
                    </h1>,
            h2: ({
              children: children_0,
              value: value_0
            }) => <>
                      <h2 id={sanitizeHeadingId((value_0 as {
                headingId?: string;
              })?.headingId)} className={`mt-[40px] text-[24px] font-semibold text-[#373A36]`}>
                        {children_0}
                      </h2>
                    </>,
            h3: ({
              children: children_1,
              value: value_1
            }) => <h3 id={sanitizeHeadingId((value_1 as {
              headingId?: string;
            })?.headingId)} className={`mb-[16px] text-[1.25rem] font-semibold text-[#373A36]`}>
                      {children_1}
                    </h3>,
            h4: ({
              children: children_2,
              value: value_2
            }) => <h4 id={sanitizeHeadingId((value_2 as {
              headingId?: string;
            })?.headingId)} className={`mb-[16px] mt-[16px] text-[1.25rem] font-semibold text-[#373A36]`}>
                      {children_2}
                    </h4>,
            normal: ({
              children: children_3,
              value: value_3
            }) => <p className={`leading-relaxed`}>{children_3}</p>
          },
          list: {
            bullet: ({
              children: children_4
            }) => <ul className="ml-5 list-disc">{children_4}</ul>,
            number: ({
              children: children_5
            }) => <ol className="mb-5 ml-5 list-decimal">{children_5}</ol>
          },
          marks: {
            unknownMark: () => null,
            link: ({
              children: children_6,
              value: value_4
            }) => <BaseLink href={value_4?.href || "#"} target={value_4?.blank ? "_blank" : undefined} rel={value_4?.blank ? "noopener noreferrer" : undefined} className="underline">
                      {children_6}
                    </BaseLink>,
            internalLink: ({
              children: children_7,
              value: value_5
            }) => <InternalLink value={value_5}>{children_7}</InternalLink>
          },
          types: {
            undefined: () => null,
            image: ({
              value: value_6
            }) => {
              const image = imageUrl(value_6);
              return <Image src={image.url() ?? ""} alt={image.url() ?? ""} width={800} height={500} className="h-auto w-full max-w-[800px] justify-center object-contain px-5 pt-6 md:px-8 md:pt-10" />;
            },
            richTable: ({
              value: value_7
            }) => <RichTableRenderer value={value_7} />,
            table: ({
              value: value_8
            }) => <TableRenderer value={value_8} />
          }
        }} />
            {fact && factInsertionIndex === index + 1 && <ProductFact data={fact} />}
            {/* ✅ SAFE + EVEN AD INSERTION */}
            {adIndexes.includes(index + 1) && ads[adPointer++]}
          </div>;
    })}
    </div>;
};
export default ResourceContent;